import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { RouteComponent } from '@app/route/route.component';
import { HeaderService } from '@app/service/header/header.service';
import { StateService } from '@app/service/state/state.service';
import {
  PillSelectorOption,
  StepperComponent,
} from 'web-component-library/projects/component-library/src/public-api';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { FormValidators } from '@app/validation/validators';
import { NoRoute } from '@assets/val/route-constants';
import {
  Housing,
  KalpAnswersNO,
  KalpInfoReason,
  KalpQuestionType,
  LeqService,
} from '@app/service/leq/leq.service';
import { CurrencyCode } from '@app/model/currency';
import { NoUserService } from '@app/service/no-user-service/no-user.service';

@Component({
  selector: 'no-loan-eligibility-questionnaire-extended',
  templateUrl: './no-loan-eligibility-questionnaire.component.html',
  styleUrls: [
    '../../page-shared.css',
    './no-loan-eligibility-questionnaire.component.scss',
  ],
})
export class NoLoanEligibilityQuestionnaireComponent
  extends RouteComponent
  implements OnInit
{
  @ViewChild(StepperComponent) stepper!: StepperComponent;

  formGroup: FormGroup;
  showError: boolean = false;

  // Pill selector data
  housingTypeOptions: PillSelectorOption[];
  selectedHousingTypeOption: PillSelectorOption;

  // Single Pill data
  hasChildren: boolean = false;
  hasInherent: boolean = false;

  loading: boolean = true;
  error: boolean = false;

  navigationInProgress: boolean;
  viewReviewAnswers: boolean = false;

  kalpAnswers: KalpAnswersNO;
  kalpQuestions: KalpQuestionType[];

  constructor(
    protected router: Router,
    protected route: ActivatedRoute,
    protected stateService: StateService,
    protected headerService: HeaderService,
    private noUserService: NoUserService,
    private leqService: LeqService
  ) {
    super(router, route, stateService, headerService);
  }

  async ngOnInit(): Promise<void> {
    await this.setup();
  }

  async setup(): Promise<void> {
    try {
      this.noUserService.getKalpInfo(KalpInfoReason.INFO).subscribe((resp) => {
        if (resp) {
          this.leqService.setKalpAnswersNO(resp);
          this.kalpAnswers = resp;

          this.viewReviewAnswers = true;
        }
        this.noUserService.getKalpQuestions().then((resp) => {
          this.kalpQuestions = resp;

          this.initializePillData();
          this.initializeFormGroup();
          this.loading = false;
        });
      });
    } catch (err) {
      this.error = true;
      this.handleError(err);
    }
  }

  initializePillData(): void {
    this.housingTypeOptions = this.getHousingTypeOptions();

    this.selectedHousingTypeOption =
      this.findSelectedOptionByValue(
        this.housingTypeOptions,
        this.kalpAnswers?.typeOfLiving
      ) ?? this.housingTypeOptions[0];

    this.hasChildren = Boolean(this.kalpAnswers?.numberOfChildren);
    this.hasInherent = Boolean(this.kalpAnswers?.numberOfAdults);
  }

  initializeFormGroup(): void {
    this.formGroup = new FormGroup({
      monthlyNetIncome: new FormControl(
        String(this.kalpAnswers?.monthlyNetIncome || '0'),
        [Validators.required, FormValidators.number(0, 1000000)]
      ),
      numberOfChildren: new FormControl(
        String(
          this.hasChildren ? this.kalpAnswers?.numberOfChildren || '0' : '0'
        ),
        [
          Validators.required,
          FormValidators.number(0, 50),
          Validators.pattern(/^[0-9]+$/),
        ]
      ),
      monthlyMortgageCost: new FormControl(
        String(this.kalpAnswers?.monthlyMortgageCost || '0'),
        [Validators.required, FormValidators.number(0, 1000000)]
      ),
    });
  }

  getHousingTypeOptions(): PillSelectorOption[] {
    return [
      { text: 'Utleieleilighet', value: Housing.RENTAL },
      { text: 'Selveir', value: Housing.CONDO },
      { text: 'Andels- eller aksjeeier', value: Housing.HOUSE },
    ];
  }

  findSelectedOptionByValue(
    pillOptions: PillSelectorOption[],
    pillOptionValue: Housing
  ): PillSelectorOption {
    return pillOptions.find(
      (pillOption) => pillOption.value === pillOptionValue
    );
  }

  async handleCtaClick(): Promise<void> {
    if (this.stepper.currentStepIndex < this.stepper.steps.length - 1) {
      this.stepper.nextStep();
      return;
    }

    if (this.formGroup.valid) {
      await this.createAndSetKalpAnswers();
      this.viewReviewAnswers = true;
    } else {
      this.stepToFirstError();
      this.showError = true;
    }
  }

  stepToFirstError(): void {
    const invalidControllers: { name: string; control: AbstractControl }[] =
      Object.keys(this.formGroup.controls)
        .filter((name) => this.formGroup.get(name).invalid)
        .map((key) => ({
          name: key,
          control: this.formGroup.get(key)!,
        }));

    if (invalidControllers.length > 0) {
      const { name, control } = invalidControllers[0];

      // Sends the user to the step that returned the invalid error.
      switch (name) {
        case 'monthlyNetIncome':
          this.stepper.goToStep(1);
          break;
        case 'numberOfAdults':
          this.stepper.goToStep(2);
          break;
        case 'numberOfChildren':
          this.stepper.goToStep(3);
          break;
        case 'monthlyMortgageCost':
          this.stepper.goToStep(4);
          break;
        default:
          console.warn(`Unhandled invalid control: ${name}`, control.errors);
      }
    }
  }

  handleBackClick(): void {
    if (this.viewReviewAnswers) {
      this.viewReviewAnswers = false;
    } else if (this.stepper.currentStepIndex !== 0) {
      this.stepper.previousStep();
    } else {
      this.handleCancellation();
    }
  }

  async handleCancellation(): Promise<void> {
    this.navigationInProgress = true;
    this.router.navigate([NoRoute.HOME], {
      queryParamsHandling: 'merge',
    });
    this.navigationInProgress = false;
  }

  async createAndSetKalpAnswers(): Promise<void> {
    const newKalpAnswers: KalpAnswersNO = {
      monthlyNetIncome: this.getNumberFromDecimalString(
        this.formGroup.controls['monthlyNetIncome'].value
      ),
      typeOfLiving: Housing[this.selectedHousingTypeOption.value],
      numberOfAdults: this.hasInherent ? 1 : 0,
      numberOfChildren: this.hasChildren ? this.getNumberOfChildren() : 0,
      monthlyMortgageCost: this.getNumberFromDecimalString(
        this.formGroup.controls['monthlyMortgageCost'].value
      ),
      currencyCode: CurrencyCode.NOK,
    };

    this.leqService.setKalpAnswersNO(newKalpAnswers);
    this.kalpAnswers = newKalpAnswers;
  }

  async handleCtaClickReview(): Promise<void> {
    this.loading = true;

    try {
      await this.noUserService
        .postKalpAnswers(this.kalpAnswers)
        .then(() => this.navigate());
    } catch (err) {
      this.error = true;
      this.handleError(err);
    }
  }

  handleUpdateClick(): void {
    this.viewReviewAnswers = false;
  }

  getNumberFromDecimalString(answer?: string): number {
    let formattedAnswer: string;

    if (answer) {
      formattedAnswer = answer.replace(',', '.').replace(/\s+/g, '');
    }

    return formattedAnswer ? Number(formattedAnswer) : 0;
  }

  getShouldAskQuestion(leqQuestion: string): boolean {
    return this.kalpQuestions.includes(KalpQuestionType[leqQuestion]);
  }

  getNumberOfChildren(): number {
    let children: number = Number(
      this.formGroup.controls['numberOfChildren'].value
    );

    if (children >= 5) {
      return 5;
    }

    return children;
  }
}
